"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, Mail, Phone, User, MessageSquare } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: formRef,
    isVisible: formVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  if (isSubmitted && !submitError) {
    return <div className="min-h-[500px] flex items-center justify-center p-6">
                <Card className="max-w-md w-full shadow-medium">
                    <CardContent className="text-center space-y-4">
                        <div className="size-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                            <Mail className="size-8 text-primary" />
                        </div>
                        <h3 className="text-2xl font-display font-semibold text-foreground">
                            Message Sent!
                        </h3>
                        <p className="text-muted-foreground">{successMessage}</p>
                        <Button onClick={resetForm} variant="outline" className="w-full hover:scale-[1.02] transition-transform duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
                            Send Another Message
                        </Button>
                    </CardContent>
                </Card>
            </div>;
  }
  return <div className="grid lg:grid-cols-2 gap-8 items-start">
            {}
            <div ref={formRef} className={`transition-all duration-[var(--duration-fast)] ease-[var(--ease-smooth)] ${formVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                <Card className="shadow-medium hover:shadow-strong transition-shadow duration-[var(--duration-medium)]">
                    <CardContent className="space-y-6">
                        <div className="space-y-2">
                            <h2 className="text-3xl font-display font-bold text-foreground">
                                Get in Touch
                            </h2>
                            <p className="text-muted-foreground">
                                Let us know how we can help with your aerial imaging needs
                            </p>
                        </div>

                        <Form onSubmit={handleSubmit}>
                            <div className="space-y-4">
                                <FormField control={control} name="name" rules={{
                required: "Name is required",
                minLength: {
                  value: 2,
                  message: "Name must be at least 2 characters"
                },
                maxLength: {
                  value: 50,
                  message: "Name must be less than 50 characters"
                }
              }} render={({
                field
              }) => <FormItem>
                                            <FormLabel className="flex items-center gap-2">
                                                <User className="size-4 text-primary" />
                                                Name
                                            </FormLabel>
                                            <FormControl>
                                                <Input placeholder="John Doe" className="focus:scale-[1.01] transition-transform duration-[var(--duration-fast)]" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>} />

                                <FormField control={control} name="email" rules={{
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Please enter a valid email address"
                }
              }} render={({
                field
              }) => <FormItem>
                                            <FormLabel className="flex items-center gap-2">
                                                <Mail className="size-4 text-primary" />
                                                Email
                                            </FormLabel>
                                            <FormControl>
                                                <Input type="email" placeholder="john@example.com" className="focus:scale-[1.01] transition-transform duration-[var(--duration-fast)]" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>} />

                                <FormField control={control} name="phone" rules={{
                required: "Phone number is required",
                pattern: {
                  value: /^[\d\s\-\+\(\)]+$/,
                  message: "Please enter a valid phone number"
                }
              }} render={({
                field
              }) => <FormItem>
                                            <FormLabel className="flex items-center gap-2">
                                                <Phone className="size-4 text-primary" />
                                                Phone
                                            </FormLabel>
                                            <FormControl>
                                                <Input type="tel" placeholder="(555) 123-4567" className="focus:scale-[1.01] transition-transform duration-[var(--duration-fast)]" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>} />

                                <FormField control={control} name="message" rules={{
                required: "Message is required",
                minLength: {
                  value: 10,
                  message: "Message must be at least 10 characters"
                }
              }} render={({
                field
              }) => <FormItem>
                                            <FormLabel className="flex items-center gap-2">
                                                <MessageSquare className="size-4 text-primary" />
                                                Message
                                            </FormLabel>
                                            <FormControl>
                                                <Textarea placeholder="Tell us about your project needs..." className="min-h-32 focus:scale-[1.01] transition-transform duration-[var(--duration-fast)]" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>} />

                                {submitError && <div className="p-4 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg">
                                        <p className="text-red-800 dark:text-red-200 text-sm">{submitError}</p>
                                    </div>}

                                <Button type="submit" disabled={isSubmitting} className="w-full hover:scale-[1.02] hover:shadow-medium transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)]">
                                    {isSubmitting ? <>
                                            <Loader className="animate-spin mr-2 size-4" />
                                            Sending Message...
                                        </> : 'Send Message'}
                                </Button>
                            </div>
                        </Form>
                    </CardContent>
                </Card>
            </div>

            {}
            <div ref={imageRef} className={`relative h-full min-h-[500px] rounded-lg overflow-hidden shadow-medium transition-all duration-[var(--duration-fast)] ease-[var(--ease-smooth)] ${imageVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
                <div className="absolute inset-0 bg-cover bg-center transform hover:scale-105 transition-transform duration-[var(--duration-slow)] ease-[var(--ease-smooth)]" style={{
        backgroundImage: "url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1520322/img/drone_in_flight_action.png)"
      }} />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-background/80 via-dark-background/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <h3 className="text-2xl font-display font-bold mb-2">
                        Professional Aerial Services
                    </h3>
                    <p className="text-white/90">
                        Serving Central Texas & DFW Metroplex with cutting-edge drone technology
                    </p>
                </div>
            </div>
        </div>;
}
export default function ContactForm() {
  return <section className="py-16 px-4 sm:px-6 lg:px-8 bg-background">
            <div className="max-w-7xl mx-auto">
                <FormProvider formId="contact-form-main" sectionName="contact-form-section" defaultValues={{
        name: "",
        email: "",
        phone: "",
        message: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2,
          maxLength: 50
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        message: {
          required: true,
          minLength: 10
        }
      }} submitText="Send Message" successMessage="Thank you for reaching out! We'll get back to you within 24 hours." errorMessage="We couldn't send your message. Please try again or contact us directly.">
                    <ContactFormInner />
                </FormProvider>
            </div>
        </section>;
}